import rest from "@/rest-client";

/** TKP-102 — taslak/paket işlemleri. Ürün, istek başlığından (`Product`) gelir. */
export const approval = {
  namespaced: true,
  state: { status: null, drafts: [], pending: [], period: null, isLoading: false, isBusy: false, batchCompletedAt: 0 },
  getters: {
    status: (state) => state.status,
    period: (state) => state.period,
    drafts: (state) => state.drafts,
    pending: (state) => state.pending,
    isLoading: (state) => state.isLoading,
    isBusy: (state) => state.isBusy,
    batchCompletedAt: (state) => state.batchCompletedAt, // TKP-242: açık paneller izler, işlem bitince yenilenir
  },
  mutations: {
    status: (state, value) => (state.status = value),
    drafts: (state, value) => (state.drafts = value || []),
    pending: (state, value) => (state.pending = value || []),
    period: (state, value) => (state.period = value),
    isLoading: (state, value) => (state.isLoading = value),
    isBusy: (state, value) => (state.isBusy = value),
    batchCompleted: (state, value) => (state.batchCompletedAt = value || Date.now()),
  },
  actions: {
    /** Onay süreci bu üründe açık mı, kullanıcı onay yetkilisi mi — her üye okuyabilir (sekme görünürlüğü). */
    async status({ commit }) {
      const response = await rest.get("/approval/status");
      commit("status", response.data);
      return response;
    },
    async fetch({ commit }) {
      commit("isLoading", true);
      try {
        const response = await rest.get("/approval/draft");
        commit("drafts", response.data);
        return response;
      } finally {
        commit("isLoading", false);
      }
    },
    /** TKP-105 — periyodik güncelleme durumu (dönem, birim atamaları, başlatabilir mi). */
    async fetchPeriod({ commit }) {
      const response = await rest.get("/approval/period");
      commit("period", response.data);
      return response;
    },
    /** TKP-159: koşum adı ve koşum başına e-posta anahtarı gövdede. */
    async startPeriod({ commit, dispatch }, payload = {}) {
      commit("isBusy", true);
      try {
        const response = await rest.post("/approval/period/start", { name: payload.name || undefined, notifyByEmail: payload.notifyByEmail !== false });
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    /** TKP-159 "Tamamlandı" sekmesi: kapanmış koşumlar (sayfalı) ve salt okunur koşum içeriği. */
    async fetchPeriodHistory(_ctx, { page = 1, limit = 20 } = {}) {
      return rest.get("/approval/period/history", { params: { page, limit } });
    },
    async fetchPeriodDetail(_ctx, periodId) {
      return rest.get(`/approval/period/${periodId}`);
    },
    /** TKP-160: birim "Güncelleme Tamamlandı"; koşum "Tamamlandı". */
    async periodUpdated({ commit, dispatch }, departmentId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/period/department/${departmentId}/updated`);
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    async completePeriod({ commit, dispatch }, periodId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/period/${periodId}/complete`);
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    async periodNoChange({ commit, dispatch }, departmentId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/period/department/${departmentId}/no-change`);
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    /** TKP-100 — gecikmiş teyit için birimin yetkililerine hatırlatma. */
    /** TKP-135: süreç düzeyi "güncelleme yok" beyanı. */
    async periodProcessNoChange({ commit, dispatch }, processId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/period/process/${processId}/no-change`);
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    /** TKP-179 md.63: süreç düzeyi "güncelleme tamamlandı"; birim / süreç satırını geri alma. */
    async periodProcessUpdated({ commit, dispatch }, processId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/period/process/${processId}/updated`);
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    async periodReopen({ commit, dispatch }, departmentId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/period/department/${departmentId}/reopen`);
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    async periodProcessReopen({ commit, dispatch }, processId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/period/process/${processId}/reopen`);
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    async periodRemind({ commit, dispatch }, departmentId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/period/department/${departmentId}/remind`);
        await dispatch("fetchPeriod");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    /** TKP-103 — onaylayanın bekleyen paketleri (bağlam adlarıyla). */
    async fetchPending({ commit }) {
      commit("isLoading", true);
      try {
        const response = await rest.get("/approval/pending");
        commit("pending", response.data);
        return response;
      } finally {
        commit("isLoading", false);
      }
    },
    /** TKP-157 — satır kararı: APPROVE (anında envantere) | EDIT_REQUEST (açıklama) | REJECT (gerekçe). */
    async decideRow({ commit, dispatch }, { packageId, editId, decision, reason, values }) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/package/${packageId}/edit/${editId}/decide`, { decision, ...(reason ? { reason } : {}), ...(values ? { values } : {}) });
        await dispatch("fetchPending");
        await dispatch("fetch");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    /** TKP-157 — "Geri Gönder": düzenleme istenen satırlarla paket yazara döner. */
    async returnPackage({ commit, dispatch }, packageId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/package/${packageId}/return`);
        await dispatch("fetchPending");
        await dispatch("fetch");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    /** TKP-103 — "Onay İşlemi Tamamlandı"; TKP-157: satır kararlarından sonra karar gövdesi boş gider. */
    async complete({ commit, dispatch }, { packageId, decisions }) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/package/${packageId}/complete`, { decisions: decisions || [] });
        await dispatch("fetchPending");
        await dispatch("fetch");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    /** TKP-156 — tarihçe: tamamlanmış paketler (mine: gönderdiklerim; decided: onayladıklarım), sayfalı. */
    async history(_, { scope = "mine", page = 1, limit = 20 } = {}) {
      return rest.get(`/approval/history?scope=${scope}&page=${page}&limit=${limit}`);
    },
    /** TKP-156 — paket içeriği (salt okunur görüntüleme). */
    async packageDetail(_, packageId) {
      return rest.get(`/approval/package/${packageId}`);
    },
    async approvers(_, packageId) {
      return rest.get(`/approval/package/${packageId}/approvers`);
    },
    async submit({ commit, dispatch }, { packageId, approver, departmentApprover }) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/package/${packageId}/submit`, { ...(approver ? { approver } : {}), ...(departmentApprover ? { departmentApprover } : {}) }); // TKP-134
        await dispatch("fetch");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    async withdraw({ commit, dispatch }, packageId) {
      commit("isBusy", true);
      try {
        const response = await rest.post(`/approval/package/${packageId}/withdraw`);
        await dispatch("fetch");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    /** TKP-242 q.1: kaydedilmiş adımlar sunucuya tek istekle — işlem arka planda, bitince bildirim (`approval-batch-completed`). */
    /** TKP-242 q.2: arka plan işlemi bitti — taslaklar bir kez, sonra panellere sinyal. */
    async batchCompleted({ commit, dispatch }) {
      await dispatch("fetch").catch(() => undefined);
      commit("batchCompleted", Date.now());
    },
    async startBatch(_, body) {
      return rest.post("/approval/batch", body);
    },
    /** TKP-242 q.3: toplu işlemin satırları tek istekte kaldırılır; taslak listesi bir kez yenilenir. */
    async removeEdits({ commit, dispatch }, ids) {
      commit("isBusy", true);
      try {
        const response = await rest.post("/approval/edits/remove", { ids });
        await dispatch("fetch");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
    async removeEdit({ commit, dispatch }, editId) {
      commit("isBusy", true);
      try {
        const response = await rest.delete(`/approval/edit/${editId}`);
        await dispatch("fetch");
        return response;
      } finally {
        commit("isBusy", false);
      }
    },
  },
};
